/**
 * What a dimension emits, before anything has been checked.
 *
 * ⛔ A CANDIDATE IS A CLAIM, NOT A FINDING. Nothing in this file is trusted: every field here
 * arrives from a model and is compared against the clone in `gate.ts` before it becomes a
 * `Finding`. The two types are deliberately distinct so that "the model said so" and "we
 * checked" cannot be confused at a call site — a `Finding` carries `components`, and there is no way
 * to build one without filling them in.
 *
 * ⭐ THE FIELD ORDER IN `EMISSION_ORDER` IS THE PRODUCT DECISION, NOT A FORMATTING CHOICE.
 * reasoning first, severity LAST. A model that states a verdict before its evidence rationalises
 * toward the verdict; one vendor measured −51% false positives from this ordering plus per-concern
 * agents (docs/ARCHITECTURE.md, "Confidence components"). It is written down in three places that
 * must agree — this array, the JSON schema below, and the five prompt files — and `engine.test.ts`
 * asserts all three, because an ordering that only lives in prose is an ordering that drifts.
 *
 * ⛔ NO CONFIDENCE FIELD, AND NO ROOM FOR ONE. The model is never asked how sure it is. See the
 * header of src/types/finding.ts for the measurements that closed that question.
 */

import type { JsonSchema } from "../backend/types.js"
import type { PreparedClone } from "../repo/clone.js"
import type { Anchor, Effort, Provenance, Severity, Suggestion } from "../types/finding.js"

/**
 * The slice of a prepared clone the engine reads.
 *
 * ⭐ A narrow type rather than `PreparedClone` so a caller can hand us a path — the same
 * reason `CloneConfig` exists in src/repo/clone.ts. It also keeps the tests honest: a gate test that
 * needed a fabricated `PreparedClone` would be tempted to fabricate the sha too.
 */
export type ReviewClone = Pick<PreparedClone, "path">

/**
 * ⛔ TWO PARTS, BOTH REQUIRED, AND THAT IS WHY IT IS NOT A STRING. "Has a failure scenario" is a
 * confidence component, and computing it from free prose would mean grepping English for the shape
 * of an argument. Splitting it moves the judgement to where it belongs — the model writes the two
 * assertions, we check that both are there. *"Let the model write assertions, not verdicts."*
 */
export interface FailureScenario {
  /** The concrete values, state or ordering that triggers it. */
  readonly inputs: string
  /** What the system then does that it must not. */
  readonly wrongOutcome: string
}

/**
 * A rule the finding says this code breaks, quoted from the file that states it.
 *
 * ⭐ The strongest evidence a finding can carry, because it is checkable: `text` is searched for in
 * `source` inside the clone. A rule the reviewed repository wrote about itself is a defect no
 * general-purpose reviewer could have found.
 */
export interface RuleCitation {
  /** Repository-relative path to the file that states the rule. */
  readonly source: string
  /** The rule's own words, copied from that file. */
  readonly text: string
}

/** One dimension's claim about one place in the code. */
export interface CandidateFinding {
  readonly dimensionId: string
  readonly severity: Severity
  readonly title: string
  readonly anchor: Anchor
  /** ⛔ Claimed to be byte-identical to the source at `anchor`. Verified in gate.ts, never trusted. */
  readonly quote: string
  readonly consequence: string
  readonly failureScenario: FailureScenario
  readonly recommendedFix: string
  readonly effort: Effort
  /** The model's reasoning, captured because the prompt demands it BEFORE the finding. */
  readonly reasoning: string
  readonly ruleCitation?: RuleCitation
  readonly suggestion?: Suggestion
  readonly provenance: Provenance
  /**
   * True when this dimension was run with `review.run_disabled` — visible in triage, never postable.
   * Carried here because the class must survive the gates rather than being re-derived from config.
   */
  readonly shadow?: boolean
}

/**
 * ⛔ THE ORDER THE MODEL MUST EMIT IN. reasoning first, severity last. Changing this array changes
 * the schema the backend enforces and contradicts five prompt files; the test suite fails on
 * purpose if it is edited alone.
 */
export const EMISSION_ORDER = [
  "reasoning",
  "title",
  "path",
  "line",
  "startLine",
  "quote",
  "consequence",
  "failureScenario",
  "recommendedFix",
  "effort",
  "ruleCitation",
  "severity",
] as const

/**
 * Grammar-constrained output for a dimension call — not a request in prose for JSON.
 *
 * ⚠️ `side` is deliberately absent: it is always RIGHT. A finding anchored to the pre-image can
 * never have been introduced by this pull request, so letting a model choose the side would be
 * handing it a switch that silently turns every finding into a pre-existing one.
 */
export const CANDIDATE_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["findings"],
  properties: {
    findings: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        // ⛔ Order is meaning here. Severity is last in `required` and last in `properties`.
        required: [
          "reasoning",
          "title",
          "path",
          "line",
          "quote",
          "consequence",
          "failureScenario",
          "recommendedFix",
          "effort",
          "severity",
        ],
        properties: {
          reasoning: {
            type: "string",
            description:
              "What you read, what you checked, and what would have to be true for this code to be correct. Written BEFORE you know whether you have a finding.",
          },
          title: { type: "string", description: "One line. The subject is the capability, never an identifier." },
          path: { type: "string", description: "Repository-relative path. Must resolve in the reviewed tree." },
          line: { type: "integer", minimum: 1, description: "Post-image line number." },
          startLine: {
            type: "integer",
            minimum: 1,
            description: "Only for a multi-line quote; the first line of the range ending at `line`.",
          },
          quote: {
            type: "string",
            description:
              "The cited line copied BYTE FOR BYTE, including leading indentation and trailing spaces. Compared character by character; one wrong character drops the finding.",
          },
          consequence: {
            type: "string",
            description: "Plain language, capability as the subject of the sentence, no identifier as the subject.",
          },
          failureScenario: {
            type: "object",
            additionalProperties: false,
            required: ["inputs", "wrongOutcome"],
            properties: {
              inputs: { type: "string", description: "Concrete values, state or ordering that triggers it." },
              wrongOutcome: { type: "string", description: "What the system then does that it must not." },
            },
          },
          recommendedFix: { type: "string" },
          effort: { type: "string", enum: ["S", "M", "L"] },
          ruleCitation: {
            type: "object",
            additionalProperties: false,
            required: ["source", "text"],
            properties: {
              source: { type: "string", description: "Repository-relative path to the file stating the rule." },
              text: { type: "string", description: "The rule's own words, copied from that file." },
            },
          },
          // ⛔ LAST. See the file header.
          severity: { type: "string", enum: ["high", "medium", "low"] },
        },
      },
    },
  },
}

/** The `Finding.failureScenario` string, assembled from the two parts the model wrote separately. */
export function renderFailureScenario(s: FailureScenario): string {
  return `${s.inputs.trim()} ⇒ ${s.wrongOutcome.trim()}`
}

/**
 * Both halves present and non-empty.
 *
 * ⛔ Mechanical, and that is the whole point: no model is asked whether its scenario is concrete.
 * Whitespace-only counts as absent, because a field filled with a space is how a required field gets
 * satisfied without being answered.
 */
export function hasFailureScenario(s: FailureScenario): boolean {
  return s.inputs.trim() !== "" && s.wrongOutcome.trim() !== ""
}

/**
 * Every post-image line the quote covers, low to high.
 *
 * ⚠️ A multi-line anchor with `startLine` above `line` is inverted rather than empty — GitHub would
 * reject it, and treating it as a zero-line range would quietly pass the quote gate against nothing.
 */
export function citedLines(anchor: Anchor): number[] {
  const end = anchor.line
  const start = anchor.startLine ?? end
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 1 || end < 1) return []
  if (start > end) return []
  const out: number[] = []
  for (let n = start; n <= end; n++) out.push(n)
  return out
}
